import React, { useState } from "react"
import PropTypes from "prop-types"
import {
    Card,
    CardContent,
    CardMedia,
    Typography,
    Box,
    IconButton,
    Chip,
    Stack,
} from "@mui/material"
import { styled } from "@mui/system"
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew"
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"
import { useMediaQuery } from "@mui/material"

const StyledCard = styled(Card)(({ theme }) => ({
    width: "100%", // Changed from fixed 400px to 100% for responsiveness
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    "&:hover": {
        boxShadow: theme.shadows[6],
    },
}))

const NavigationButton = styled(IconButton)(() => ({
    color: "white",
    backgroundColor: "rgba(255,255,255,0.3)",
    border: "none",
    borderRadius: "50%",
    "&:hover": {
        backgroundColor: "rgba(255,255,255,0.5)",
    },
}))

const ImageContainer = styled(Box)({
    position: "relative",
    "&:hover .navigation-buttons": {
        opacity: 1,
    },
    "&:hover .status-chip": {
        opacity: 1,
    },
})

const NavigationButtonsContainer = styled(Box)({
    position: "absolute",
    top: "50%",
    left: 4,
    right: 4,
    zIndex: 2,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 1,
    transform: "translateY(-50%)",
    opacity: 0,
    transition: "opacity 0.2s ease-in-out",
})

const StatusChip = styled(Box)({
    position: "absolute",
    top: 4,
    left: 4,
    zIndex: 2,
    opacity: 0,
    transition: "opacity 0.2s ease-in-out",
})

const PropertyCard = ({
    images,
    price,
    beds,
    baths,
    sqft,
    acres,
    address,
    status,
    url,
    listingType,
}) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"))
    const handlePrevImage = (event) => {
        event.stopPropagation()
        setCurrentImageIndex((prevIndex) =>
            prevIndex > 0 ? prevIndex - 1 : images.length - 1
        )
    }

    const handleNextImage = (event) => {
        event.stopPropagation()
        setCurrentImageIndex((prevIndex) =>
            prevIndex < images.length - 1 ? prevIndex + 1 : 0
        )
    }

    const handleClick = () => {
        window.open(url, "_blank", "noopener,noreferrer")
    }

    const formatPrice = (price) => {
        if (!price) return '';
        
        // If price is already formatted (contains $), return as is
        if (price.toString().includes('$')) {
            return price;
        }
        
        // Convert to number and format
        const numericValue = parseFloat(price);
        if (isNaN(numericValue)) return price;
        
        // Format as currency
        const formatted = `$${numericValue.toLocaleString('en-US')}`;
        
        // Append /month for rentals
        if (listingType === 'rentals') {
            return `${formatted}/month`;
        }
        
        return formatted;
    }

    return (
        <StyledCard onClick={handleClick}>
            <ImageContainer>
                <CardMedia
                    component="img"
                    height="300"
                    image={images[currentImageIndex]}
                    alt={`Property ${currentImageIndex + 1}`}
                />
                <StatusChip className="status-chip">
                    <Chip label={status} color="primary" size="small" />
                </StatusChip>

                <NavigationButtonsContainer className="navigation-buttons">
                    <NavigationButton onClick={handlePrevImage}>
                        <ArrowBackIosNewIcon />
                    </NavigationButton>
                    <NavigationButton onClick={handleNextImage}>
                        <ArrowForwardIosIcon />
                    </NavigationButton>
                </NavigationButtonsContainer>
            </ImageContainer>
            <CardContent>
                <Stack alignItems="start">
                    <Typography variant="h5" component="div" fontWeight="700">
                        {formatPrice(price)}
                    </Typography>
                    <Stack direction="row" spacing={isMobile ? 0.5 : 1.5}>
                        <Typography variant="body2" color="text">
                            {beds} Bed
                        </Typography>
                        <Typography variant="body2" color="text">
                            •
                        </Typography>
                        <Typography variant="body2" color="text">
                            {baths} Bath
                        </Typography>
                        <Typography variant="body2" color="text">
                            •
                        </Typography>
                        <Typography variant="body2" color="text">
                            {sqft.toLocaleString()} Sq Ft
                        </Typography>
                        <Typography variant="body2" color="text">
                            {acres} Acres
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        {address}
                    </Typography>
                </Stack>
            </CardContent>
        </StyledCard>
    )
}

PropertyCard.propTypes = {
    images: PropTypes.arrayOf(PropTypes.string).isRequired,
    price: PropTypes.string.isRequired,
    beds: PropTypes.number.isRequired,
    baths: PropTypes.number.isRequired,
    sqft: PropTypes.number.isRequired,
    acres: PropTypes.string,
    address: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    listingType: PropTypes.string,
}

export default PropertyCard
